import { deepmerge } from '@mui/utils';
import { useContext, useEffect, useState } from 'react';

import userAuth from './userAuth';
import { FontWeightType } from '../components/atoms/AtomTypography/AtomTypography';
import { FeaturesEnum } from '../constants/enums/FeaturesEnum';
import { FeatureFlagsContext } from '../contexts/FeatureFlagsContext';
import { useLazyFeatureComplete } from '../operations/query/feature/useGetFeatureComplete';
import defaultPalette from '../theme/palette';
import { readWhiteLabelCache, writeWhiteLabelCache } from '../utils/whiteLabelCache';
import { ROLE_ENUM } from '../views/Auth/Login/interfaces/IGoogleUser';

interface IFaviconConfig {
  pageTitleEN?: string;
  pageTitleES?: string;
}

export interface IWizardSkillsStepConfig {
  headingFontSize?: string;
  headingFontWeight?: FontWeightType;
  descriptionFontSize?: string;
  contentPaddingPercent?: number;
}

export interface IWizardConfig {
  skillsStep?: IWizardSkillsStepConfig;
}

export interface IWhiteLabelPaletteOverrides {
  light?: {
    background?: Record<string, string>;
    text?: Record<string, string>;
  };
}

interface IStoredWl {
  enabled: boolean;
  palette: any;
  // Institution overrides before the deepmerge with the default palette.
  customPalette: IWhiteLabelPaletteOverrides | null;
  images: any;
  loginConfig: any;
  faviconConfig?: IFaviconConfig | null;
  wizardConfig?: IWizardConfig | null;
  isLoading: boolean;
}

const useWhiteLabel = (): IStoredWl => {
  const { features } = useContext(FeatureFlagsContext);
  const { auth } = userAuth();
  // Computed once per mount: readWhiteLabelCache() parses JSON and would otherwise return a new
  // object reference every render, tripping the effect below into an infinite update loop.
  const [storedWL] = useState(() => readWhiteLabelCache());
  const isAdmin = auth?.userRole === ROLE_ENUM.ADMIN;
  // A warm, verified cache seeds the initial render directly (instead of only via an effect after
  // mount) so consumers paint fully-branded on their first paint, with no default-then-branded flash.
  const initialTheme = !isAdmin && features.qsemDesignWhiteLabel ? storedWL : null;

  const [palette, setPalette] = useState(
    initialTheme ? deepmerge(defaultPalette, initialTheme.palette) : defaultPalette,
  );
  const [customPalette, setCustomPalette] = useState<IWhiteLabelPaletteOverrides | null>(initialTheme?.palette ?? null);
  const [images, setImages] = useState(initialTheme?.images ?? null);
  const [loginConfig, setLoginConfig] = useState(initialTheme?.loginConfig ?? null);
  const [faviconConfig, setFaviconConfig] = useState<IFaviconConfig | null>(initialTheme?.faviconConfig ?? null);
  const [wizardConfig, setWizardConfig] = useState<IWizardConfig | null>(initialTheme?.wizardConfig ?? null);
  const [enabled, setEnabled] = useState(Boolean(initialTheme));

  const [getFeatures, { data: featureWhiteLabel, loading }] = useLazyFeatureComplete(false);

  useEffect(() => {
    if (auth?.userRole === ROLE_ENUM.ADMIN) {
      setPalette(defaultPalette);
      setCustomPalette(null);

      return;
    }

    if (features.qsemDesignWhiteLabel && storedWL) {
      setEnabled(true);
      setPalette(deepmerge(defaultPalette, storedWL.palette));
      setCustomPalette(storedWL.palette ?? null);
      setImages(storedWL.images);
      setLoginConfig(storedWL.loginConfig);
      setFaviconConfig(storedWL.faviconConfig);
      setWizardConfig(storedWL.wizardConfig ?? null);
    }
  }, [features.qsemDesignWhiteLabel, storedWL, auth]);

  useEffect(() => {
    if (features.qsemDesignWhiteLabel && !storedWL) {
      getFeatures({ variables: { feature: FeaturesEnum.qsemDesignWhiteLabel } });
    }
  }, [features.qsemDesignWhiteLabel, getFeatures, storedWL]);

  useEffect(() => {
    const customTheme = featureWhiteLabel?.getFeatureNameAndValue
      ? JSON.parse(featureWhiteLabel?.getFeatureNameAndValue.featureValue)
      : null;

    if (features.qsemDesignWhiteLabel && customTheme) {
      setEnabled(true);
      setPalette(deepmerge(defaultPalette, customTheme.palette));
      setCustomPalette(customTheme.palette ?? null);
      setImages(customTheme.images);
      setLoginConfig(customTheme.loginConfig);
      setFaviconConfig(customTheme.faviconConfig);
      setWizardConfig(customTheme.wizardConfig ?? null);

      writeWhiteLabelCache(customTheme);
    }
  }, [features.qsemDesignWhiteLabel, featureWhiteLabel]);

  return {
    enabled,
    palette,
    customPalette,
    images,
    loginConfig,
    faviconConfig,
    wizardConfig,
    isLoading: loading,
  };
};

export default useWhiteLabel;
